import { useEffect, useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { opsApi } from "../../lib/api";

/** The moment before the money leaves. Blockers must be fixed; the not-Open state and any change
 *  order touching this scope must be ANSWERED — never silently blocked, never silently passed. */
const STATUS_WORD = {
  estimate: "in Estimate", ready: "Ready", on_hold: "On Hold", completed: "Completed",
  closed: "Closed", cancelled: "Cancelled",
};

export default function IssueDialog({ project, po, onClose, onDone }) {
  const [pre, setPre] = useState(null);
  const [note, setNote] = useState("");
  const [acked, setAcked] = useState([]);
  const [checkAcked, setCheckAcked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    opsApi.poPreflight(project.id, po.id).then(setPre)
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not run the pre-flight"));
  }, [project.id, po.id]);

  const addCoItems = async (co) => {
    setBusy(true);
    try {
      await opsApi.poAddCoItems(project.id, po.id, { line_ids: co.line_ids || [] });
      toast.success(`CO ${co.number} items added to ${po.po_number}`);
      setPre(await opsApi.poPreflight(project.id, po.id));
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Those items could not be added"); }
    finally { setBusy(false); }
  };

  const requestOpening = async () => {
    setBusy(true);
    try {
      setDraft(await opsApi.emailDraft(project.id, "project_opening_request", po.id));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not draft that request"); }
    finally { setBusy(false); }
  };

  const issue = async () => {
    setBusy(true);
    try {
      await opsApi.poIssue(project.id, po.id, {
        authorization_note: note.trim(),
        acknowledge_co_ids: acked,
        acknowledge_po_check: checkAcked,
      });
      toast.success(`${po.po_number} issued — the money is committed to the budget`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "That purchase order was not issued"); }
    finally { setBusy(false); }
  };

  const unanswered = (pre?.change_orders || []).filter((c) => !acked.includes(c.change_order_id));
  const blocked = (pre?.blockers?.length || 0) > 0
    || (pre?.not_open?.blocked && !note.trim())
    || unanswered.length > 0
    || ((pre?.po_check?.findings?.length || 0) > 0 && !checkAcked);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
         onClick={onClose}>
      <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-5 shadow-xl"
           onClick={(e) => e.stopPropagation()} data-testid="ops-po-issue-dialog">
        <h3 className="text-base font-semibold text-slate-900">
          Issue {po.po_number}{po.manufacturer ? ` · ${po.manufacturer}` : ""}
        </h3>
        <p className="mt-1 text-xs text-slate-500">
          Issuing commits ${Number(po.total).toLocaleString()} to the budget and stamps the FRS rows
          as ordered.
        </p>

        {!pre ? (
          <div className="flex items-center gap-2 py-8 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Running the pre-flight…
          </div>
        ) : (
          <div className="mt-3 space-y-3 text-xs">
            {pre.blockers.length > 0 && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-rose-900"
                   data-testid="ops-po-blockers">
                <p className="font-semibold">These have to be fixed first</p>
                <ul className="mt-1 space-y-0.5">
                  {pre.blockers.map((b, i) => <li key={i}>{b.message}</li>)}
                </ul>
              </div>
            )}

            {pre.cautions.length > 0 && (
              <ul className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-900"
                  data-testid="ops-po-cautions">
                {pre.cautions.map((c, i) => <li key={i}>{c.message}</li>)}
              </ul>
            )}

            {pre.not_open && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-900"
                   data-testid="ops-po-not-open-gate">
                <p className="font-semibold">
                  This project is {STATUS_WORD[pre.not_open.project_status] || "not Open"}.
                </p>
                <p className="mt-0.5">
                  Cancel, proceed with a written authorization (logged against the PO), or request
                  the job be opened.
                </p>
                <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                          placeholder="Who authorized this, and how"
                          className="mt-2 bg-white text-xs" data-testid="ops-po-auth-note" />
                <Button size="sm" variant="outline" className="mt-2" onClick={requestOpening}
                        disabled={busy} data-testid="ops-po-request-opening">
                  Request job opening
                </Button>
              </div>
            )}

            {pre.change_orders.map((c) => (
              <div key={c.change_order_id + c.kind}
                   className="rounded-lg border border-violet-200 bg-violet-50 p-3 text-violet-900"
                   data-testid={`ops-po-co-check-${c.number}`}>
                <p className="font-semibold">{c.message}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {c.choices.includes("add_co_items") && (
                    <Button size="sm" variant="outline" disabled={busy}
                            onClick={() => addCoItems(c)}
                            data-testid={`ops-po-add-co-${c.number}`}>Add CO items</Button>
                  )}
                  <Button size="sm" variant={acked.includes(c.change_order_id) ? "default" : "ghost"}
                          onClick={() => setAcked((a) => a.includes(c.change_order_id)
                            ? a.filter((x) => x !== c.change_order_id) : [...a, c.change_order_id])}
                          data-testid={`ops-po-ack-co-${c.number}`}>
                    {acked.includes(c.change_order_id) ? "Acknowledged" : "Continue anyway (logged)"}
                  </Button>
                </div>
              </div>
            ))}

            {pre.po_check.applies && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3"
                   data-testid="ops-po-check">
                <p className="font-semibold text-slate-800">
                  Automated PO check · {pre.po_check.findings.length} finding(s)
                </p>
                <ul className="mt-1 space-y-0.5 text-slate-700">
                  {pre.po_check.findings.map((f, i) => <li key={i}>{f.message}</li>)}
                </ul>
                {pre.po_check.findings.length > 0 && (
                  <label className="mt-2 flex items-center gap-2 text-slate-700">
                    <input type="checkbox" checked={checkAcked}
                           onChange={(e) => setCheckAcked(e.target.checked)}
                           data-testid="ops-po-check-ack" />
                    I have reviewed these and the purchase order is correct
                  </label>
                )}
              </div>
            )}

            {!pre.blockers.length && !pre.not_open && !pre.change_orders.length && (
              <p className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-emerald-800"
                 data-testid="ops-po-clean">
                Nothing is in the way: the job is Open, the submittals are approved, no change
                order touches this scope and every line is priced.
              </p>
            )}
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="ops-po-issue-cancel">
            Cancel
          </Button>
          <Button size="sm" onClick={issue} disabled={busy || !pre || blocked}
                  data-testid="ops-po-issue-confirm">
            {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              : <ShieldCheck className="mr-1.5 h-3.5 w-3.5" />} Issue this purchase order
          </Button>
        </div>

        {draft && (
          <p className="mt-2 text-xs text-slate-500" data-testid="ops-po-opening-draft">
            A request to open the job is drafted for {(draft.to || []).join(", ") || "accounting"}:
            “{draft.subject}”. Send it from the Emails section of the project.
          </p>
        )}
      </div>
    </div>
  );
}
